import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import styles from './NotesTab.module.scss';

import {
  BusinessProfileNotes,
  deleteBusinessProfileNotes,
  selectBusinessProfileStatus,
  updateBusinessProfileNotes,
} from '../../slices/business-profile/businessProfileSlice';
import { useAppSelector } from '../hooks/useAppSelector';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import NotesEditButton from './NotesEditButton';
import NoteInput from '../form-inputs/note-input/NoteInput';
import { selectUser } from '../../slices/user/userSlice';
import { Helpers } from '../helpers/helpers';
import { NWClient } from '../../client/NWClient';
import { deleteEmployeeNote, fetchEmployeeNotes } from '../../slices/employees/employeesSlice';
import { deleteCustomerNote, fetchCustomerNotes } from '../../slices/customers/customersSlice';
import { deleteSupplierNote, fetchSupplierNotes } from '../../slices/suppliers/suppliersSlice';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';

interface NotesCardProps {
  note?: BusinessProfileNotes;
  empnote?: any;
  supnote?: any;
  cusnote?: any;
}

export default function NotesCard({ empnote, note, cusnote, supnote }: NotesCardProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);

  const businessProfileStatus = useAppSelector(selectBusinessProfileStatus);
  const isLoading = businessProfileStatus === 'loading';

  const [isEdited, setIsEdited] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [cusFormData, setCusFormData] = useState({
    id: cusnote?.id || 0,
    notes: cusnote?.notes || '',
    customer_id: cusnote?.customer_id || 0,
    updated: cusnote?.updated || '',
    user: cusnote?.user || 0,
    user_name: cusnote?.user_name || '',
  });
  const [supFormData, setSupFormData] = useState({
    id: supnote?.id || 0,
    notes: supnote?.notes || '',
    supplier_id: supnote?.supplier_id || 0,
    updated: supnote?.updated || '',
    user: supnote?.user || 0,
    user_name: supnote?.user_name || '',
  });
  const [formData, setFormData] = useState<BusinessProfileNotes>({
    id: note?.id || 0,
    company: note?.company || 0,
    user: note?.user || 0,
    user_name: note?.user_name || '',
    updated: note?.updated || '',
    note: note?.note || '',
  });
  const [empFormData, setEmpFormData] = useState({
    id: empnote?.id || 0,
    employee_id: empnote?.employee_id || 0,
    employee_name: empnote?.employee_name || '',
    note: empnote?.note || '',
    updated: empnote?.updated || '',
    user: empnote?.user || 0,
    user_name: empnote?.user_name || '',
  });
  const formRef = useRef<HTMLDivElement | null>(null);

  const toggleEditing = () => {
    setIsEdited((prev) => !prev);
  };

  const cancelChanges = () => {
    if (note?.id) {
      setFormData({
        id: note?.id || 0,
        company: note?.company || 0,
        user: note?.user || 0,
        user_name: note?.user_name || '',
        updated: note?.updated || '',
        note: note?.note || '',
      });
    }
    if (empnote?.id) {
      setEmpFormData({
        id: empnote?.id || 0,
        employee_id: empnote?.employee_id || 0,
        employee_name: empnote?.employee_name || '',
        note: empnote?.note || '',
        updated: empnote?.updated || '',
        user: empnote?.user || 0,
        user_name: empnote?.user_name || '',
      });
    }

    if (cusnote?.id) {
      setCusFormData({
        id: cusnote?.id || 0,
        notes: cusnote?.notes || '',
        customer_id: cusnote?.customer_id || 0,
        updated: cusnote?.updated || '',
        user: cusnote?.user || 0,
        user_name: cusnote?.user_name || '',
      });
    }

    if (supnote?.id) {
      setSupFormData({
        id: supnote?.id || 0,
        notes: supnote?.notes || '',
        supplier_id: supnote?.supplier_id || 0,
        updated: supnote?.updated || '',
        user: supnote?.user || 0,
        user_name: supnote?.user_name || '',
      });
    }
    setIsEdited(false);
  };

  const saveChanges = useCallback(() => {
    dispatch(showOverlay());
    if (!note?.id && !empnote?.id && !cusnote?.id && !supnote?.id) return;

    if (note?.id) {
      dispatch(
        updateBusinessProfileNotes({
          companyId: note.company,
          id: note.id,
          data: formData,
        })
      )
        .unwrap()
        .finally(() => dispatch(hideOverlay()));
    }

    // TODO : REFACTOR TO USE REDUX

    if (empnote?.id) {
      setEmpFormData({ ...empFormData, user: user.id });
      NWClient.put(
        `employee/${empnote.employee_id}/notes`,
        empnote.id,
        empFormData,
        true,
        true
      ).then(() => {
        dispatch(fetchEmployeeNotes(empnote.employee_id))
          .unwrap()
          .finally(() => dispatch(hideOverlay()));
      });
    }

    if (cusnote?.id) {
      setCusFormData({ ...cusFormData, user: user.id });
      NWClient.put(`customer-notes`, cusnote.id, cusFormData, true, true).then(() => {
        dispatch(fetchCustomerNotes(cusnote.customer_id))
          .unwrap()
          .finally(() => dispatch(hideOverlay()));
      });
    }

    if (supnote?.id) {
      setSupFormData({ ...supFormData, user: user.id });
      NWClient.put(`supplier-notes`, supnote.id, supFormData, true, true).then(() => {
        dispatch(fetchSupplierNotes(supnote.supplier_id))
          .unwrap()
          .finally(() => dispatch(hideOverlay()));
      });
    }

    setIsEdited(false);
    setShowModal(false);
  }, [
    dispatch,
    note,
    empnote,
    cusnote,
    supnote,
    formData,
    empFormData,
    cusFormData,
    supFormData,
    user.id,
  ]);

  const handleDelete = () => {
    if (!note?.id && !empnote?.id && !cusnote?.id && !supnote?.id) return;

    if (note?.id) {
      dispatch(showOverlay());
      dispatch(deleteBusinessProfileNotes({ companyId: note.company, id: note.id }))
        .unwrap()
        .finally(() => dispatch(hideOverlay()));
    }

    // REFACTOR TO USE REDUX
    if (empnote?.id) {
      dispatch(showOverlay());
      dispatch(
        deleteEmployeeNote({
          employeeId: empnote.employee_id,
          noteId: empnote.id,
        })
      )
        .unwrap()
        .finally(() => dispatch(hideOverlay()));
    }

    if (cusnote?.id) {
      dispatch(showOverlay());
      dispatch(
        deleteCustomerNote({
          customerId: cusnote.customer_id,
          noteId: cusnote.id,
        })
      )
        .unwrap()
        .finally(() => dispatch(hideOverlay()));
    }

    if (supnote?.id) {
      dispatch(showOverlay());
      dispatch(
        deleteSupplierNote({
          supplierId: supnote.supplier_id,
          noteId: supnote.id,
        })
      )
        .unwrap()
        .finally(() => dispatch(hideOverlay()));
    }
    setIsEdited(false);
  };

  const confirmExit = useCallback(() => {
    setIsEdited(false);
    setShowModal(false);
  }, []);

  const handleChange =
    <K extends keyof BusinessProfileNotes>(field: K) =>
    (value: BusinessProfileNotes[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleEmpChange = (field: string) => (value: string) => {
    setEmpFormData((prev) => ({ ...prev, [field]: value, user: user.id }));
  };

  const handleCusChange = (field: string) => (value: string) => {
    setCusFormData((prev) => ({ ...prev, [field]: value, user: user.id }));
  };

  const handleSupChange = (field: string) => (value: string) => {
    setSupFormData((prev) => ({ ...prev, [field]: value, user: user.id }));
  };
  // USE EFFECT TO HANDLE CLICKING OUTSIDE WHILE EDITING ATTRIBUTES
  useEffect(() => {
    let showModalTimeout: ReturnType<typeof setTimeout> | undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (isEdited && formRef.current && !formRef.current.contains(event.target as Node)) {
        if (event.target instanceof HTMLElement) {
          const ignoredElements = ['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'LABEL'];
          if (ignoredElements.includes(event.target.tagName)) return;
        }

        showModalTimeout = setTimeout(() => {
          setShowModal(true);
        }, 0);
      }
    };

    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (showModalTimeout) clearTimeout(showModalTimeout);
    };
  }, [isEdited]);

  return (
    <Card className={`${styles.card} ${isEdited ? styles.card_border : ''}`} ref={formRef}>
      <CardContent
        className={isLoading ? styles.card_content_min_height : styles.notes_card}
        sx={{
          paddingBottom: '16px !important',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: '8px', md: '0' },
        }}
      >
        {note && (
          <Grid component='div' style={{ flexGrow: 1 }}>
            {!isEdited ? (
              <div className={styles.typography}>
                <Typography variant='h6' sx={{ marginBottom: '0.5rem' }}>
                  {formData.note}
                </Typography>
                <Grid component='div' style={{ display: 'flex' }}>
                  <Typography variant='body1'>
                    {Helpers.timeStampToDateV3(formData.updated)}
                  </Typography>
                  <Typography variant='body1' sx={{ mx: 0.5 }}>
                    - By
                  </Typography>
                  <Typography variant='body1'>{formData.user_name}</Typography>
                </Grid>
              </div>
            ) : (
              <NoteInput
                value={formData.note}
                onChange={handleChange('note')}
                disabled={!isEdited}
              />
            )}
          </Grid>
        )}

        {empnote && (
          <Grid component='div' style={{ flexGrow: 1 }}>
            {!isEdited ? (
              <div className={styles.typography}>
                <Typography variant='h6' sx={{ marginBottom: '0.5rem' }}>
                  {empFormData.note}
                </Typography>
                <Grid component='div' style={{ display: 'flex' }}>
                  <Typography variant='body1'>
                    {Helpers.timeStampToDateV3(empFormData.updated)}
                  </Typography>
                  <Typography variant='body1' sx={{ mx: 0.5 }}>
                    - By
                  </Typography>
                  <Typography variant='body1'>{empFormData.user_name}</Typography>
                </Grid>
              </div>
            ) : (
              <NoteInput
                value={empFormData.note}
                onChange={handleEmpChange('note')}
                disabled={!isEdited}
              />
            )}
          </Grid>
        )}

        {cusnote && (
          <Grid component='div' style={{ flexGrow: 1 }}>
            {!isEdited ? (
              <div className={styles.typography}>
                <Typography variant='h6' sx={{ marginBottom: '0.5rem' }}>
                  {cusFormData.notes}
                </Typography>
                <Grid component='div' style={{ display: 'flex' }}>
                  <Typography variant='body1'>
                    {Helpers.timeStampToDateV3(cusFormData.updated)}
                  </Typography>
                  <Typography variant='body1' sx={{ mx: 0.5 }}>
                    - By
                  </Typography>
                  <Typography variant='body1'>{cusFormData.user_name}</Typography>
                </Grid>
              </div>
            ) : (
              <NoteInput
                value={cusFormData.notes}
                onChange={handleCusChange('notes')}
                disabled={!isEdited}
              />
            )}
          </Grid>
        )}

        {supnote && (
          <Grid component='div' style={{ flexGrow: 1 }}>
            {!isEdited ? (
              <div className={styles.typography}>
                <Typography variant='h6' sx={{ marginBottom: '0.5rem' }}>
                  {supFormData.notes}
                </Typography>
                <Grid component='div' style={{ display: 'flex' }}>
                  <Typography variant='body1'>
                    {Helpers.timeStampToDateV3(supFormData.updated)}
                  </Typography>
                  <Typography variant='body1' sx={{ mx: 0.5 }}>
                    - By
                  </Typography>
                  <Typography variant='body1'>{supFormData.user_name}</Typography>
                </Grid>
              </div>
            ) : (
              <NoteInput
                value={supFormData.notes}
                onChange={handleSupChange('notes')}
                disabled={!isEdited}
              />
            )}
          </Grid>
        )}
        <Grid component='div' container sx={{ flexShrink: 0 }} style={{ alignItems: 'center' }}>
          <Grid
            component='div'
            container
            sx={{ justifyContent: 'flex-end', width: { xs: '100%', md: 'auto' } }}
          >
            {!isEdited ? (
              <NotesEditButton
                id='notesCardItemActions'
                onEdit={toggleEditing}
                onDelete={handleDelete}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  marginLeft: '8px',
                  justifyContent: 'flex-end',
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <Button
                  id='notesCardCancelBtn'
                  type='button'
                  className='btn-default btn-action-basic btn-cancel'
                  role='button'
                  variant='contained'
                  size='large'
                  onClick={cancelChanges}
                >
                  {t('buttons.cancel')}
                </Button>
                <Button
                  id='notesCardSaveBtn'
                  type='submit'
                  className='btn-default btn-action-basic'
                  role='button'
                  disabled={!isEdited}
                  variant='contained'
                  size='large'
                  onClick={saveChanges}
                >
                  {t('buttons.save')}
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
    </Card>
  );
}
